import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import { getContentById, getAllTags, getAllPromptsAdmin } from '../graphql/queries'
import { updateContent, deleteContent } from '../graphql/mutation'
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Avatar,
  Autocomplete
} from '@mui/material'
import ArticleIcon from '@mui/icons-material/Article'
import ImageIcon from '@mui/icons-material/Image'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import ProgressBar from '../components/circularProgress/circularProgress'
import { uploadToS3 } from '../utils/uploadToS3'
import { gql } from '@apollo/client'

const getAllSections =  gql`
query GetAllSections {
  getAllSections {
    id
    name
    rowNumber
    designType
    isPublic
    banner
    videoUrl
    gifUrl
    webmUrl
    webpUrl
    createdAt
    updatedAt
}
}
`

export const getAllStyles = gql`
    query GetAllStylesAdmin($pageSize: Int, $page: Int, $name: String) {
    getAllStylesAdmin(pageSize: $pageSize, page: $page, name: $name) {
        styles {
        id
        name
        banner
        designType
        rowNumber
        toolType
        isPublic
        videoUrl
        gifUrl
        webmUrl
        webpUrl
        secondBanner
        createdAt
        updatedAt
    }
    }
}
`
export default function ContentDetailPage() {
  const { id } = useParams()
  const client = useApolloClient()
  const navigate = useNavigate()

  const [content, setContent] = useState({
    contentId: '',
    contentType: 'PROMPT',
    videoUrl: '',
    banner: '',
    secondBanner: '',
    gifUrl: '',
    webmUrl: '',
    webpUrl: '',
    detailImage: '',
    designType: 'MEDIUM',
    rowNumber: 1,
    tagId: '',
    sectionId: '',
    styleId: '',
    promptId: '',
    id: '',
    createdAt: '',
    updatedAt: ''
  })

  const [contentData, setContentData] = useState(null)
  const [progress, setProgress] = useState(false)
  const [deleteProgress, setDeleteProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState({
    banner: null,
    secondBanner: null,
    video: null,
    gif: null,
    webm: null,
    webp: null,
    detailImage: null
  })

  const { loading, error, data } = useQuery(getContentById, {
    variables: { id: id },
    onCompleted: data => {
      setContentData(data.getContentById)
    },
  })

  const { data: tagsData } = useQuery(getAllTags)
  const { data: sectionsData } = useQuery(getAllSections)
  const { data: stylesData } = useQuery(getAllStyles)
  const { data: promptsData } = useQuery(getAllPromptsAdmin)

  useEffect(() => {
    if (contentData) {
      setContent({
        contentId: contentData.contentId || '',
        contentType: contentData.contentType,
        videoUrl: contentData.videoUrl || '',
        banner: contentData.banner || '',
        secondBanner: contentData.secondBanner || '',
        gifUrl: contentData.gifUrl || '',
        webmUrl: contentData.webmUrl || '',
        webpUrl: contentData.webpUrl || '',
        detailImage: contentData.detailImage || '',
        designType: contentData.designType,
        rowNumber: contentData.rowNumber || 1,
        tagId: contentData.tagId || '',
        sectionId: contentData.sectionId || '',
        styleId: contentData.styleId || '',
        promptId: contentData.promptId || '',
        id: contentData.id,
        createdAt: contentData.createdAt,
        updatedAt: contentData.updatedAt
      })
    }
  }, [contentData])

  const [updateContentMutation] = useMutation(updateContent)
  const [deleteContentMutation] = useMutation(deleteContent)

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  const handleFileSelect = (type, event) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedFiles(prev => ({ ...prev, [type]: file }))
    }
  }

  const handleChange = (field, value) => {
    setContent(prev => ({
      ...prev,
      [field]: value
    }))

    // Clear other relationship fields when selecting one
    if (field === 'sectionId' && value) {
      setContent(prev => ({
        ...prev,
        styleId: '',
        promptId: ''
      }))
    } else if (field === 'styleId' && value) {
      setContent(prev => ({
        ...prev,
        sectionId: '',
        promptId: ''
      }))
    } else if (field === 'promptId' && value) {
      setContent(prev => ({
        ...prev,
        sectionId: '',
        styleId: ''
      }))
    }
  }

  const handleUpdateContent = async () => {
    // Validate that only one relationship is set
    const relationships = [content.sectionId, content.styleId, content.promptId].filter(id => id && id.trim())
    if (relationships.length > 1) {
      setSnackBarMessage('Content can only be associated with one entity (section, style, or prompt)')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    // Determine the contentId and contentType based on the selected relationship
    let contentId = content.contentId
    let contentType = content.contentType
    
    if (content.sectionId) {
      contentId = content.sectionId
      contentType = 'SECTION'
    } else if (content.styleId) {
      contentId = content.styleId
      contentType = 'STYLE'
    } else if (content.promptId) {
      contentId = content.promptId
      contentType = 'PROMPT'
    } else {
      // If no relationship is selected, use the existing contentId and contentType
      contentId = contentData?.contentId || null
      contentType = contentData?.contentType || 'PROMPT'
    }

    setProgress(true)
    try {
      let finalBannerUrl = content.banner
      let finalSecondBannerUrl = content.secondBanner
      let finalVideoUrl = content.videoUrl
      let finalGifUrl = content.gifUrl
      let finalWebmUrl = content.webmUrl
      let finalWebpUrl = content.webpUrl
      let finalDetailImageUrl = content.detailImage

      // Upload banner file if selected
      if (selectedFiles.banner) {
        setSnackBarMessage('Uploading banner image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalBannerUrl = await uploadToS3(selectedFiles.banner, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload banner: ${uploadError.message}`)
        }
      }

      // Upload second banner file if selected
      if (selectedFiles.secondBanner) {
        setSnackBarMessage('Uploading second banner image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalSecondBannerUrl = await uploadToS3(selectedFiles.secondBanner, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload second banner: ${uploadError.message}`)
        }
      }

      // Upload video file if selected
      if (selectedFiles.video) {
        setSnackBarMessage('Uploading video...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalVideoUrl = await uploadToS3(selectedFiles.video, 'video')
        } catch (uploadError) {
          throw new Error(`Failed to upload video: ${uploadError.message}`)
        }
      }

      // Upload gif file if selected
      if (selectedFiles.gif) {
        setSnackBarMessage('Uploading GIF...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalGifUrl = await uploadToS3(selectedFiles.gif, 'gif')
        } catch (uploadError) {
          throw new Error(`Failed to upload GIF: ${uploadError.message}`)
        }
      }

      // Upload webm file if selected
      if (selectedFiles.webm) {
        setSnackBarMessage('Uploading WEBM video...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalWebmUrl = await uploadToS3(selectedFiles.webm, 'video')
        } catch (uploadError) {
          throw new Error(`Failed to upload WEBM: ${uploadError.message}`)
        }
      }

      // Upload webp file if selected
      if (selectedFiles.webp) {
        setSnackBarMessage('Uploading WEBP image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalWebpUrl = await uploadToS3(selectedFiles.webp, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload WEBP: ${uploadError.message}`)
        }
      }

      // Upload detail image file if selected
      if (selectedFiles.detailImage) {
        setSnackBarMessage('Uploading detail image...')
        setSnackBarSeverity('info')
        setSnackBarOpen(true)
        
        try {
          finalDetailImageUrl = await uploadToS3(selectedFiles.detailImage, 'img')
        } catch (uploadError) {
          throw new Error(`Failed to upload detail image: ${uploadError.message}`)
        }
      }

      const variables = {
        id: content.id,
        contentId: contentId || null,
        contentType: contentType,
        videoUrl: finalVideoUrl || null,
        banner: finalBannerUrl || null,
        secondBanner: finalSecondBannerUrl || null,
        gifUrl: finalGifUrl || null,
        webmUrl: finalWebmUrl || null,
        webpUrl: finalWebpUrl || null,
        detailImage: finalDetailImageUrl || null,
        designType: content.designType,
        rowNumber: content.rowNumber || 1,
        tagId: content.tagId || null
      }



      const response = await updateContentMutation({
        variables
      })

      if (response.data) {
        setSnackBarMessage('Content updated successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        // Clear selected files after successful update
        setSelectedFiles({ banner: null, secondBanner: null, video: null, gif: null, webm: null, webp: null, detailImage: null })
        
        // Update cache
        client.writeQuery({
          query: getContentById,
          variables: { id: id },
          data: {
            getContentById: {
              ...response.data.updateContent,
              __typename: 'Content'
            }
          }
        })
      }
    } catch (error) {
      setSnackBarMessage(`Error updating content: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleDeleteContent = async () => {
    setDeleteProgress(true)
    try {
      const response = await deleteContentMutation({
        variables: {
          id: content.id
        }
      })

      if (response.data) {
        setSnackBarMessage('Content deleted successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        // Navigate back to content list after a short delay
        setTimeout(() => {
          navigate('/content')
        }, 1500)
      }
    } catch (error) {
      setSnackBarMessage(`Error deleting content: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setDeleteProgress(false)
      setDeleteDialogOpen(false)
    }
  }

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true)
  }

  if (loading) return <ProgressBar />
  if (error) return <Alert severity="error">Error loading content: {error.message}</Alert>
  if (!contentData) return <Alert severity="error">Content not found</Alert>

  const getContentTypeColor = (type) => {
    switch (type) {
      case 'PROMPT': return 'primary'
      case 'STYLE': return 'secondary'
      case 'SECTION': return 'success'
      default: return 'default'
    }
  }

  const getDesignTypeColor = (type) => {
    switch (type) {
      case 'SMALL': return 'default'
      case 'MEDIUM': return 'primary'
      case 'LARGE': return 'secondary'
      case 'BANNER': return 'success'
      case 'VIDEO': return 'warning'
      case 'CIRCLE': return 'info'
      case 'PORTRAIT': return 'error'
      case 'SQUARE': return 'default'
      default: return 'default'
    }
  }

  const availableTags = tagsData?.getAllTags || []
  const availableSections = sectionsData?.getAllSections || []
  const availableStyles = stylesData?.getAllStylesAdmin?.styles || []
  const availablePrompts = promptsData?.getAllPromptsAdmin?.prompts || []

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 4, 
      maxWidth: '900px', 
      mx: 'auto', 
      px: 2, 
      py: 2 
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ArticleIcon fontSize="large" sx={{ color: '#1976d2' }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Content Details
        </Typography>
      </Box>

      {/* Main Content */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Content Type"
              value={content.contentType === 'PROMPT' ? 'Prompt' : content.contentType === 'STYLE' ? 'Style' : 'Section'}
              disabled
              helperText="Content type cannot be changed"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              options={[
                { value: 'SMALL', label: 'Small' },
                { value: 'MEDIUM', label: 'Medium' },
                { value: 'LARGE', label: 'Large' },
                { value: 'BANNER', label: 'Banner' },
                { value: 'VIDEO', label: 'Video' },
                { value: 'CIRCLE', label: 'Circle' },
                { value: 'PORTRAIT', label: 'Portrait' },
                { value: 'SQUARE', label: 'Square' }
              ]}
              value={content.designType ? {
                value: content.designType,
                label: content.designType.charAt(0) + content.designType.slice(1).toLowerCase()
              } : null}
              onChange={(event, newValue) => handleChange('designType', newValue?.value || '')}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="Design Type" />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip 
                label={content.contentType} 
                color={getContentTypeColor(content.contentType)}
                size="small"
                variant="outlined"
              />
              <Chip 
                label={content.designType} 
                color={getDesignTypeColor(content.designType)}
                size="small"
                variant="filled"
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Content ID"
              value={content.contentId}
              disabled
              helperText="Content identifier cannot be changed"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Row Number"
              value={content.rowNumber}
              onChange={(e) => handleChange('rowNumber', parseInt(e.target.value) || 1)}
              helperText="Display order position"
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Media */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon color="primary" />
              Media Content
            </Typography>
          </Grid>

          {/* Media Cards */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Banner Image */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                    Banner Image
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Banner URL"
                    value={content.banner}
                    onChange={(e) => handleChange('banner', e.target.value)}
                    placeholder="https://example.com/banner.jpg"
                    sx={{ mb: 2 }}
                    size="small"
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">OR</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                  
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="banner-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('banner', e)}
                  />
                  <label htmlFor="banner-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Choose Image File
                    </Button>
                  </label>
                  
                  {selectedFiles.banner && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Selected: {selectedFiles.banner.name}
                      </Typography>
                    </Box>
                  )}
                  
                  {content.banner && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: 'grey.300'
                    }}>
                      <img 
                        src={content.banner} 
                        alt="Banner preview"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '120px', 
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Video */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'secondary.main' }}>
                    Video
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Video URL"
                    value={content.videoUrl}
                    onChange={(e) => handleChange('videoUrl', e.target.value)}
                    placeholder="https://example.com/video.mp4"
                    sx={{ mb: 2 }}
                    size="small"
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">OR</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                  
                  <input
                    accept="video/*"
                    style={{ display: 'none' }}
                    id="video-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('video', e)}
                  />
                  <label htmlFor="video-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<VideoLibraryIcon />}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Choose Video File
                    </Button>
                  </label>
                  
                  {selectedFiles.video && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Selected: {selectedFiles.video.name}
                      </Typography>
                    </Box>
                  )}
                  
                  {content.videoUrl && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: 2, 
                      p: 3, 
                      bgcolor: 'secondary.light', 
                      borderRadius: 2,
                      color: 'secondary.contrastText'
                    }}>
                      <VideoLibraryIcon fontSize="large" />
                      <Typography variant="body1" fontWeight="500">Video Attached</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* GIF */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'success.main' }}>
                    GIF Animation
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="GIF URL"
                    value={content.gifUrl}
                    onChange={(e) => handleChange('gifUrl', e.target.value)}
                    placeholder="https://example.com/animation.gif"
                    sx={{ mb: 2 }}
                    size="small"
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">OR</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                  
                  <input
                    accept="image/gif"
                    style={{ display: 'none' }}
                    id="gif-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('gif', e)}
                  />
                  <label htmlFor="gif-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<ImageIcon />}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Choose GIF File
                    </Button>
                  </label>
                  
                  {selectedFiles.gif && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Selected: {selectedFiles.gif.name}
                      </Typography>
                    </Box>
                  )}
                  
                  {content.gifUrl && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: 'success.main'
                    }}>
                      <img 
                        src={content.gifUrl} 
                        alt="GIF preview"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '120px', 
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Second Banner */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'warning.main' }}>
                    Second Banner
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Second Banner URL"
                    value={content.secondBanner}
                    onChange={(e) => handleChange('secondBanner', e.target.value)}
                    placeholder="https://example.com/second-banner.jpg"
                    sx={{ mb: 2 }}
                    size="small"
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">OR</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                  
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="second-banner-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('secondBanner', e)}
                  />
                  <label htmlFor="second-banner-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Choose Image File
                    </Button>
                  </label>
                  
                  {selectedFiles.secondBanner && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Selected: {selectedFiles.secondBanner.name}
                      </Typography>
                    </Box>
                  )}
                  
                  {content.secondBanner && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: 'warning.main'
                    }}>
                      <img 
                        src={content.secondBanner} 
                        alt="Second banner preview"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '120px', 
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* WEBM Video */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'info.main' }}>
                    WEBM Video
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="WEBM URL"
                    value={content.webmUrl}
                    onChange={(e) => handleChange('webmUrl', e.target.value)}
                    placeholder="https://example.com/video.webm"
                    sx={{ mb: 2 }}
                    size="small"
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">OR</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                  
                  <input
                    accept="video/webm"
                    style={{ display: 'none' }}
                    id="webm-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('webm', e)}
                  />
                  <label htmlFor="webm-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<VideoLibraryIcon />}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Choose WEBM File
                    </Button>
                  </label>
                  
                  {selectedFiles.webm && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Selected: {selectedFiles.webm.name}
                      </Typography>
                    </Box>
                  )}
                  
                  {content.webmUrl && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: 2, 
                      p: 3, 
                      bgcolor: 'info.light', 
                      borderRadius: 2,
                      color: 'info.contrastText'
                    }}>
                      <VideoLibraryIcon fontSize="large" />
                      <Typography variant="body1" fontWeight="500">WEBM Attached</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* WEBP Image */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'error.main' }}>
                    WEBP Image
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="WEBP URL"
                    value={content.webpUrl}
                    onChange={(e) => handleChange('webpUrl', e.target.value)}
                    placeholder="https://example.com/image.webp"
                    sx={{ mb: 2 }}
                    size="small"
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">OR</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                  
                  <input
                    accept="image/webp"
                    style={{ display: 'none' }}
                    id="webp-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('webp', e)}
                  />
                  <label htmlFor="webp-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Choose WEBP File
                    </Button>
                  </label>
                  
                  {selectedFiles.webp && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Selected: {selectedFiles.webp.name}
                      </Typography>
                    </Box>
                  )}
                  
                  {content.webpUrl && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: 'error.main'
                    }}>
                      <img 
                        src={content.webpUrl} 
                        alt="WEBP preview"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '120px', 
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Detail Image */}
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                    Detail Image
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Detail Image URL"
                    value={content.detailImage}
                    onChange={(e) => handleChange('detailImage', e.target.value)}
                    placeholder="https://example.com/detail-image.jpg"
                    sx={{ mb: 2 }}
                    size="small"
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">OR</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>
                  
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="detail-image-upload"
                    type="file"
                    onChange={(e) => handleFileSelect('detailImage', e)}
                  />
                  <label htmlFor="detail-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Choose Image File
                    </Button>
                  </label>
                  
                  {selectedFiles.detailImage && (
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Selected: {selectedFiles.detailImage.name}
                      </Typography>
                    </Box>
                  )}
                  
                  {content.detailImage && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      p: 2, 
                      bgcolor: 'grey.50', 
                      borderRadius: 2,
                      border: '2px dashed',
                      borderColor: 'grey.400'
                    }}>
                      <img 
                        src={content.detailImage} 
                        alt="Detail image preview"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '120px', 
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Relationships */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Relationships (Select only one)
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              options={[
                { value: '', label: 'No Tag' },
                ...availableTags.map((tag) => ({ value: tag.id, label: tag.name }))
              ]}
              value={content.tagId ? availableTags.find(tag => tag.id === content.tagId) ? { value: content.tagId, label: availableTags.find(tag => tag.id === content.tagId).name } : { value: content.tagId, label: 'Unknown Tag' } : { value: '', label: 'No Tag' }}
              onChange={(event, newValue) => handleChange('tagId', newValue?.value || '')}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="Tag (Optional)" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              options={[
                { value: '', label: 'No Section' },
                ...availableSections.map((section) => ({ value: section.id, label: section.name }))
              ]}
              value={content.sectionId ? availableSections.find(section => section.id === content.sectionId) ? { value: content.sectionId, label: availableSections.find(section => section.id === content.sectionId).name } : { value: content.sectionId, label: 'Unknown Section' } : { value: '', label: 'No Section' }}
              onChange={(event, newValue) => handleChange('sectionId', newValue?.value || '')}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="Section" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              options={[
                { value: '', label: 'No Style' },
                ...availableStyles.map((style) => ({ value: style.id, label: style.name }))
              ]}
              value={content.styleId ? availableStyles.find(style => style.id === content.styleId) ? { value: content.styleId, label: availableStyles.find(style => style.id === content.styleId).name } : { value: content.styleId, label: 'Unknown Style' } : { value: '', label: 'No Style' }}
              onChange={(event, newValue) => handleChange('styleId', newValue?.value || '')}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="Style" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              options={[
                { value: '', label: 'No Prompt' },
                ...availablePrompts.map((prompt) => ({ value: prompt.id, label: prompt.name || 'Unnamed Prompt' }))
              ]}
              value={content.promptId ? availablePrompts.find(prompt => prompt.id === content.promptId) ? { value: content.promptId, label: availablePrompts.find(prompt => prompt.id === content.promptId).name || 'Unnamed Prompt' } : { value: content.promptId, label: 'Unknown Prompt' } : { value: '', label: 'No Prompt' }}
              onChange={(event, newValue) => handleChange('promptId', newValue?.value || '')}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField {...params} label="Prompt" />
              )}
            />
          </Grid>

          {/* Metadata */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Metadata
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Content ID"
              value={content.id}
              disabled
              helperText="Unique identifier"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Created At"
              value={new Date(content.createdAt).toLocaleString()}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Updated At"
              value={new Date(content.updatedAt).toLocaleString()}
              disabled
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mt: 4, 
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={deleteProgress}
          >
            Delete
          </Button>
          
          <Button
            variant="contained"
            startIcon={progress ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleUpdateContent}
            disabled={progress}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            {progress ? 'Updating...' : 'Update Content'}
          </Button>
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Content</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this content? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteContent}
            color="error"
            disabled={deleteProgress}
            startIcon={deleteProgress ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteProgress ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={6000}
        onClose={handleSnackBarClose}
      >
        <Alert onClose={handleSnackBarClose} severity={snackBarSeverity}>
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
